import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Alert, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip, IconButton, Menu, Grid } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import TimelineIcon from '@mui/icons-material/Timeline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import LaunchIcon from '@mui/icons-material/Launch';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import config from '../config';
import DataTable from '../components/DataTable/DataTable';
import AssessmentModal from '../components/AssessmentModal';

const TIER_NATURE_OPTIONS = [
    "Auditors / Certification Bodies (Private Sector)",
    "Beneficiaries of Sponsorship/Patronage",
    "Clients - Department Stores",
    "Clients - Retailers",
    "Clients - Wholesalers",
    "External Growth Target",
    "Intercompany",
    "Public Officials",
    "Suppliers / Service Providers - Accommodation (excl. Travel)",
    "Suppliers / Service Providers - Banking / Insurance",
    "Suppliers / Service Providers - Brand Promotion",
    "Suppliers / Service Providers - Building Maintenance",
    "Suppliers / Service Providers - Cleaning Services",
    "Suppliers / Service Providers - Communication and Media / Digital",
    "Suppliers / Service Providers - Consulting (e.g., Legal, Strategic, Finance, PR, HR)",
    "Suppliers / Service Providers - Design / Architecture Activities",
    "Suppliers / Service Providers - E-Commerce Services",
    "Suppliers / Service Providers - Energy, Utilities, and Environment (e.g., Electricity, Gas)",
    "Suppliers / Service Providers - Event Agency",
    "Suppliers / Service Providers - Food and Beverages / Personal Services & Crafts",
    "Suppliers / Service Providers - Influencers",
    "Suppliers / Service Providers - IT Infrastructure & Services (e.g., Equipment, Licenses, SaaS, Maintenance, Consulting)",
    "Suppliers / Service Providers - Lab Equipment and Products",
    "Suppliers / Service Providers - Leases and Rents",
    "Suppliers / Service Providers - Logistics / Transport",
    "Suppliers / Service Providers - Model Agency",
    "Suppliers / Service Providers - Office Furniture",
    "Suppliers / Service Providers - Packaging",
    "Suppliers / Service Providers - Photography and Artistic / Creative Activities",
    "Suppliers / Service Providers - POP / Promotional Materials",
    "Suppliers / Service Providers - Production Subcontracting",
    "Suppliers / Service Providers - Raw Materials",
    "Suppliers / Service Providers - Real Estate",
    "Suppliers / Service Providers - Scientific Activities",
    "Suppliers / Service Providers - Security Equipment and Services",
    "Suppliers / Service Providers - Technical Installations and Equipment",
    "Suppliers / Service Providers - Temporary Staff",
    "Suppliers / Service Providers - Training / Education / Coaching",
    "Suppliers / Service Providers - Translation and Interpretation",
    "Suppliers / Service Providers - Travel (e.g., Travel Agencies, Accommodation, Staff Transport)"
];

const Home = () => {
    const [tables, setTables] = useState([]);
    const [selectedTable, setSelectedTable] = useState('');
    const [defaultTable, setDefaultTable] = useState(null);
    const [lastSourceTable, setLastSourceTable] = useState('');
    const [data, setData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [headerStyles, setHeaderStyles] = useState({});

    // For cleanup
    const abortControllerRef = useRef(null);
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        fetchTables();
        return () => {
            isMounted.current = false;
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [sortConfig, setSortConfig] = useState({});
    const [filters, setFilters] = useState({});
    const [visibleColumns, setVisibleColumns] = useState([]);

    // Group creation state
    const [openGroupDialog, setOpenGroupDialog] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [newViewName, setNewViewName] = useState('');
    const [selectedRows, setSelectedRows] = useState([]);
    const [anchorElBulk, setAnchorElBulk] = useState(null);
    const [isGlobalSelection, setIsGlobalSelection] = useState(false);
    const [anchorElScore, setAnchorElScore] = useState(null);

    // Assessment modal state
    const [assessmentModalOpen, setAssessmentModalOpen] = useState(false);
    const [anchorElRow, setAnchorElRow] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);

    // Screening details modal state
    const [screeningModalOpen, setScreeningModalOpen] = useState(false);
    const [screeningDetails, setScreeningDetails] = useState(null);
    const [loadingScreening, setLoadingScreening] = useState(false);

    // Tier Nature Edit State
    const [editTierModalOpen, setEditTierModalOpen] = useState(false);
    const [editingTierRow, setEditingTierRow] = useState(null);
    const [newTierValue, setNewTierValue] = useState('');

    const handleRowActionClick = (event, row) => {
        setAnchorElRow(event.currentTarget);
        setSelectedRow(row);
    };

    const handleRowActionClose = () => {
        setAnchorElRow(null);
        // Do not clear selectedRow here, as the modal might still need it
    };

    const handleBulkAddToPortfolio = async () => {
        setAnchorElBulk(null);
        if (selectedRows.length === 0 && !isGlobalSelection) return;

        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            if (isGlobalSelection) {
                // Use the new bulk-add endpoint with filters
                const response = await axios.post(`${config.API_BASE_URL}/moodys/portfolio/bulk-add`, {
                    tableName: selectedTable,
                    filters: filters
                });
                const { summary } = response.data;
                setSuccessMessage(`Traitement global terminé : ${summary.success} ajoutés, ${summary.alreadyPresent} déjà présents, ${summary.failed} erreurs.`);
                fetchData();
            } else {
                // Original per-row logic
                let successCount = 0;
                let failCount = 0;
                let alreadyPresentCount = 0;

                for (const id of selectedRows) {
                    const row = data.find(r => r.id === id);
                    if (!row) continue;

                    try {
                        const name = row['original_name_partner'] || row['partners'] || row['Nom'] || row['nom'] || row['Name'] || row['name'] || row['Supplier Name'] || row['Raison Sociale'] || row['NOM'] || '';
                        const address = row['Adresse'] || row['adresse'] || row['Address'] || row['address'] || row['ADRESSE'] || row['supplier_address'] || row['adresse_fournisseur'] || row['road_or_avenue'] || '';
                        const city = row['Ville'] || row['ville'] || row['City'] || row['city'] || row['VILLE'] || row['city_name'] || '';
                        const country = row['organization_country'] || row['pays_d_intervention'] || row['localisation'] || row['Pays'] || row['pays'] || row['Country'] || row['country'] || row['PAYS'] || row['country_of_supplier_contact'] || 'France';

                        const response = await axios.post(`${config.API_BASE_URL}/moodys/portfolio/add`, {
                            name, address, city, country,
                            tableName: selectedTable,
                            rowId: id
                        });

                        if (response.data.data.status === 'already_present') {
                            alreadyPresentCount++;
                        } else {
                            successCount++;
                        }
                    } catch (err) {
                        console.error(`Failed to add row ${id}:`, err);
                        failCount++;
                    }
                }
                setSuccessMessage(`Traitement groupé terminé : ${successCount} ajoutés, ${alreadyPresentCount} déjà présents, ${failCount} erreurs.`);
                fetchData();
            }
        } catch (err) {
            console.error("Bulk action error:", err);
            setError(err.response?.data?.error || "Erreur lors du traitement groupé.");
        } finally {
            setLoading(false);
            setSelectedRows([]);
            setIsGlobalSelection(false);
        }
    };


    useEffect(() => {
        fetchTables();
    }, []);

    useEffect(() => {
        if (selectedTable) {
            fetchData();
        }
    }, [selectedTable, page, limit, sortConfig, filters]);


    const fetchTables = async () => {
        try {
            const response = await axios.get(`${config.API_BASE_URL}/fournisseurs`);
            const tablesData = response.data;
            setTables(tablesData);

            // Fetch default table setting
            const defaultResponse = await axios.get(`${config.API_BASE_URL}/settings/default-table`);
            const defaultTableName = defaultResponse.data.value;
            setDefaultTable(defaultTableName);

            // Select default table if it exists, otherwise select first non-view table
            const sourceTables = tablesData.filter(t => !t.name.startsWith('view_'));
            if (defaultTableName && tablesData.some(t => t.name === defaultTableName)) {
                setSelectedTable(defaultTableName);
                if (!defaultTableName.startsWith('view_')) {
                    setLastSourceTable(defaultTableName);
                } else {
                    const view = tablesData.find(t => t.name === defaultTableName);
                    if (view && view.source) {
                        setLastSourceTable(view.source);
                    }
                }
            } else if (sourceTables.length > 0) {
                setSelectedTable(sourceTables[0].name);
                setLastSourceTable(sourceTables[0].name);
            }
        } catch (err) {
            console.error(err);
            setError('Failed to load tables.');
        }
    };

    const handleSetDefaultTable = async () => {
        if (!selectedTable) return;

        try {
            await axios.post(`${config.API_BASE_URL}/settings/default-table`, {
                value: selectedTable
            });
            setDefaultTable(selectedTable);
            setSuccessMessage(`Table "${typeof selectedTable === 'string' ? selectedTable : (selectedTable?.name || "")}" définie comme table par défaut.`);
        } catch (err) {
            console.error('Error setting default table:', err);
            setError('Erreur lors de la définition de la table par défaut.');
        }
    };

    const handleCreateView = async () => {
        if (!newViewName || !selectedTable) return;
        setLoading(true);
        setError(null);
        console.log('[DEBUG] handleCreateView - View Name:', newViewName);
        console.log('[DEBUG] handleCreateView - Selected Table:', selectedTable);
        console.log('[DEBUG] handleCreateView - Filters:', filters);
        try {
            await axios.post(`${config.API_BASE_URL}/fournisseurs/${selectedTable}/view`, {
                viewName: newViewName,
                filters: filters,
                visibleColumns: visibleColumns
            });
            setSuccessMessage(`Vue "${newViewName}" créée avec succès.`);
            setOpenViewDialog(false);
            setNewViewName('');
            fetchTables();
        } catch (err) {
            console.error('Error creating view:', err);
            setError(err.response?.data?.error || 'Erreur lors de la création de la vue.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTable = async (tableName) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la table/vue "${tableName}" ?`)) return;
        setLoading(true);
        setError(null);
        try {
            await axios.delete(`${config.API_BASE_URL}/fournisseurs/${tableName}`);
            setSuccessMessage(`Table/Vue "${tableName}" supprimée.`);
            if (selectedTable === tableName) {
                setSelectedTable('');
            }
            fetchTables();
        } catch (err) {
            console.error('Error deleting table:', err);
            setError('Erreur lors de la suppression.');
        } finally {
            setLoading(false);
        }
    };

    const fetchData = async () => {
        if (!selectedTable) return;

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        setLoading(true);
        try {
            const params = {
                page,
                limit,
                sortColumn: sortConfig.key,
                sortDirection: sortConfig.direction,
                filters: JSON.stringify(filters)
            };

            const response = await axios.get(`${config.API_BASE_URL}/fournisseurs/${selectedTable}`, {
                params,
                signal: abortControllerRef.current.signal
            });

            if (!isMounted.current) return;

            setData(response.data.data);
            setTotal(response.data.total);


            if (response.data.data.length > 0) {
                const dataColumns = Object.keys(response.data.data[0]);
                // Filter out internal Moody's status/score columns that have cleaner virtual equivalents
                const filteredColumns = dataColumns.filter(c => ![
                    'risk_score',
                    'cf_ra_status',
                    'cf_ra_score',
                    'cf_ra_scorekey_cf_scoreandclasses_label',
                    'statut_assessment',
                    'score_de_risque'
                ].includes(c.toLowerCase()));

                // Add assessment columns ONLY if they are present in headerStyles OR if it's not a view
                const isView = selectedTable.startsWith('view_');
                const assessmentColumns = ['assessment_status', 'assessment_risk_score', 'assessment_completion_date'].filter(col => {
                    if (!isView) return true;
                    return response.data.headerStyles && response.data.headerStyles[col];
                });

                let orderedColumns = [...filteredColumns];

                // Add Actions only if it's not a view or if it's in headerStyles
                const finalColumns = [...orderedColumns, ...assessmentColumns];
                if (!isView || (response.data.headerStyles && response.data.headerStyles['Actions'])) {
                    finalColumns.push('Actions');
                }

                setColumns(finalColumns);

                // Add labels for assessment columns
                const enhancedHeaderStyles = {
                    ...response.data.headerStyles,
                    cf_ra_id: { label: 'ID Assessment', color: null },
                    assessment_status: { label: 'Statut Assessment', color: null },
                    assessment_risk_score: { label: 'Score de Risque', color: null },
                    assessment_completion_date: { label: 'Date Complétion', color: null }
                };
                setHeaderStyles(enhancedHeaderStyles);
            } else {
                setColumns([]);
                setHeaderStyles(response.data.headerStyles || {});
            }
        } catch (err) {
            if (axios.isCancel(err)) {
                console.log('Request cancelled:', err.message);
                return;
            }
            console.error(err);
            const errorMsg = err.response?.data?.details || err.message;
            setError(`Failed to load data: ${errorMsg}`);
            if (err.response?.status === 500) {
                alert(`Backend Error (500): ${errorMsg}`);
            }
        } finally {
            if (isMounted.current) setLoading(false);
        }
    };


    const handleAddToPortfolio = async (row) => {
        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        try {
            // Find likely columns for name, address, etc.
            const name = row['original_name_partner'] || row['partners'] || row['Nom'] || row['nom'] || row['Name'] || row['name'] || row['Supplier Name'] || row['Raison Sociale'] || row['NOM'] || '';
            const address = row['Adresse'] || row['adresse'] || row['Address'] || row['address'] || row['ADRESSE'] || row['supplier_address'] || row['adresse_fournisseur'] || row['road_or_avenue'] || '';
            const city = row['Ville'] || row['ville'] || row['City'] || row['city'] || row['VILLE'] || row['city_name'] || '';
            const country = row['organization_country'] || row['pays_d_intervention'] || row['localisation'] || row['Pays'] || row['pays'] || row['Country'] || row['country'] || row['PAYS'] || row['country_of_supplier_contact'] || 'France';

            const response = await axios.post(`${config.API_BASE_URL}/moodys/portfolio/add`, {
                name,
                address,
                city,
                country,
                tableName: selectedTable,
                rowId: row.id
            });

            const { data: result } = response.data;
            if (result.status === 'already_present') {
                setSuccessMessage(`Fournisseur "${name}" est déjà dans le portefeuille (ID: ${result.CF_PORTFOLIO_ID || 'N/A'})`);
            } else {
                setSuccessMessage(`Fournisseur "${name}" ajouté avec succès au portefeuille Moody's !`);
            }

            // Refresh data to show the new BvD_ID in the table
            fetchData();
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.error || 'Erreur lors de l\'ajout au portefeuille Moody\'s.');
        } finally {
            setLoading(false);
        }
    };

    const handleAssessmentSubmit = async (model, options) => {
        setAssessmentModalOpen(false);
        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        const rowsToProcess = [];
        if (selectedRow) {
            rowsToProcess.push(selectedRow);
        } else if (selectedRows.length > 0) {
            selectedRows.forEach(id => {
                const r = data.find(row => row.id === id);
                if (r) rowsToProcess.push(r);
            });
        }

        if (rowsToProcess.length === 0) {
            setLoading(false);
            setError("Aucune ligne sélectionnée pour l'assessment.");
            return;
        }

        try {
            let successCount = 0;
            let failCount = 0;

            for (const rowData of rowsToProcess) {
                try {
                    const name = rowData['FOURNISSEUR'] || rowData['fournisseur'] || rowData['original_name_partner'] || rowData['partners'] || rowData['Nom'] || rowData['nom'] || rowData['Name'] || rowData['name'] || rowData['Supplier Name'] || rowData['Raison Sociale'] || rowData['NOM'] || '';
                    const address = rowData['ADRESSE'] || rowData['adresse'] || rowData['Adresse'] || rowData['Address'] || rowData['address'] || rowData['supplier_address'] || rowData['adresse_fournisseur'] || rowData['road_or_avenue'] || '';
                    const city = rowData['VILLE'] || rowData['ville'] || rowData['Ville'] || rowData['City'] || rowData['city'] || rowData['VILLE'] || rowData['city_name'] || '';
                    const country = rowData['organization_country'] || rowData['pays_d_intervention'] || rowData['localisation'] || rowData['Pays'] || rowData['pays'] || rowData['Country'] || rowData['country'] || rowData['PAYS'] || rowData['country_of_supplier_contact'] || 'France';
                    const bvdId = rowData['bvd_id'] || rowData['BvD_ID'] || rowData['BvDID'] || rowData['bvdid'];

                    await axios.post(`${config.API_BASE_URL}/moodys/assessment/start`, {
                        bvdId,
                        name,
                        address,
                        city,
                        country,
                        model,
                        options,
                        tableName: selectedTable,
                        rowId: rowData.id
                    });
                    successCount++;
                } catch (err) {
                    console.error(`Assessment failed for row ${rowData.id}:`, err);
                    failCount++;
                }
            }

            if (successCount > 0) {
                setSuccessMessage(`${successCount} assessment(s) démarré(s) avec succès !${failCount > 0 ? ` (${failCount} erreur(s))` : ''}`);
                fetchData();
            } else if (failCount > 0) {
                setError(`Échec du lancement des assessments (${failCount} erreur(s)).`);
            }
        } catch (err) {
            console.error("Assessment error:", err);
            setError(err.response?.data?.error || "Erreur lors du lancement de l'assessment.");
        } finally {
            setLoading(false);
            setSelectedRow(null);
            setSelectedRows([]);
        }
    };

    const handleCompleteAssessment = async (row) => {
        if (!row) return;
        const cfRaId = row['cf_ra_id'] || row['CF_RA_Id'];

        if (!cfRaId) {
            setError("Impossible de finaliser : aucun ID d'assessment (CF_RA_Id) trouvé pour cette ligne.");
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${config.API_BASE_URL}/moodys/assessment/complete`, {
                cfRaId
            });

            if (response.data.success) {
                const status = response.data.data.CF_RA_Status || 'Completed';
                setSuccessMessage(`Assessment ${cfRaId} finalisé avec succès. Nouveau statut: ${status}`);

                fetchData(); // Refresh the table
            }
        } catch (err) {
            console.error("Completion error:", err);
            setError(err.response?.data?.error || "Erreur lors de la finalisation de l'assessment.");
        } finally {
            setLoading(false);
            setSelectedRow(null);
        }
    };
    const handleBulkRefreshStatus = async () => {
        setAnchorElBulk(null);
        if (selectedRows.length === 0 && !isGlobalSelection) return;

        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            try {
                const response = await axios.post(`${config.API_BASE_URL}/moodys/assessment/bulk-refresh`, {
                    tableName: selectedTable,
                    ids: isGlobalSelection ? [] : selectedRows,
                    isGlobalSelection: isGlobalSelection,
                    filters: filters
                });

                const { summary } = response.data;
                setSuccessMessage(`Rafraîchissement terminé : ${summary.success} mis à jour, ${summary.failed} échecs.`);
            } catch (bulkErr) {
                // FALLBACK: If bulk-refresh is not found (404), process rows one by one
                if (bulkErr.response?.status === 404) {
                    console.log("Bulk refresh endpoint not found, falling back to individual refreshes...");

                    let rowsToRefresh = [];
                    if (isGlobalSelection) {
                        rowsToRefresh = data.filter(r => r.cf_ra_id || r.CF_RA_Id);
                    } else {
                        rowsToRefresh = data.filter(r => selectedRows.includes(r.id) && (r.cf_ra_id || r.CF_RA_Id));
                    }

                    if (rowsToRefresh.length === 0) {
                        setSuccessMessage("Aucun assessment à rafraîchir dans la sélection.");
                        return;
                    }

                    let successCount = 0;
                    let failCount = 0;

                    for (const row of rowsToRefresh) {
                        try {
                            const cfRaId = row.cf_ra_id || row.CF_RA_Id;
                            await axios.post(`${config.API_BASE_URL}/moodys/assessment/refresh`, {
                                cfRaId,
                                tableName: selectedTable,
                                id: row.id
                            });
                            successCount++;
                        } catch (e) {
                            failCount++;
                        }
                    }
                    setSuccessMessage(`Rafraîchissement réalisé (mode compatibilité) : ${successCount} succès, ${failCount} échecs.`);
                } else {
                    throw bulkErr;
                }
            }

            fetchData();
        } catch (err) {
            console.error("Bulk refresh error:", err);
            setError(err.response?.data?.error || "Erreur lors du rafraîchissement groupé.");
        } finally {
            setLoading(false);
            setSelectedRows([]);
            setIsGlobalSelection(false);
        }
    };

    const handleBulkCompleteAssessment = async () => {
        setAnchorElBulk(null);
        if (selectedRows.length === 0 && !isGlobalSelection) return;

        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            try {
                const response = await axios.post(`${config.API_BASE_URL}/moodys/assessment/bulk-complete`, {
                    tableName: selectedTable,
                    ids: isGlobalSelection ? [] : selectedRows,
                    isGlobalSelection: isGlobalSelection,
                    filters: filters
                });

                const { summary } = response.data;
                setSuccessMessage(`Finalisation terminée : ${summary.success} réussis, ${summary.failed} échecs.`);
            } catch (bulkErr) {
                // FALLBACK: If bulk-complete is not found (404), process rows one by one
                if (bulkErr.response?.status === 404) {
                    console.log("Bulk complete endpoint not found, falling back to individual completions...");

                    let rowsToComplete = [];
                    if (isGlobalSelection) {
                        rowsToComplete = data.filter(r => r.cf_ra_id || r.CF_RA_Id);
                    } else {
                        rowsToComplete = data.filter(r => selectedRows.includes(r.id) && (r.cf_ra_id || r.CF_RA_Id));
                    }

                    if (rowsToComplete.length === 0) {
                        setSuccessMessage("Aucun assessment à finaliser dans la sélection.");
                        return;
                    }

                    let successCount = 0;
                    let failCount = 0;

                    for (const row of rowsToComplete) {
                        try {
                            const cfRaId = row.cf_ra_id || row.CF_RA_Id;
                            await axios.post(`${config.API_BASE_URL}/moodys/assessment/complete`, {
                                cfRaId
                            });
                            successCount++;
                        } catch (e) {
                            failCount++;
                        }
                    }
                    setSuccessMessage(`Finalisation réalisée (mode compatibilité) : ${successCount} succès, ${failCount} échecs.`);
                } else {
                    throw bulkErr;
                }
            }

            fetchData(); // Refresh the table
        } catch (err) {
            console.error("Bulk complete error:", err);
            setError(err.response?.data?.error || "Erreur lors de la finalisation groupée.");
        } finally {
            setLoading(false);
            setSelectedRows([]);
            setIsGlobalSelection(false);
        }
    };

    const handleRefreshStatus = async (row) => {
        if (!row) return;
        const cfRaId = row['cf_ra_id'] || row['CF_RA_Id'];
        const id = row.id;

        if (!cfRaId) {
            setError("Impossible de rafraîchir : aucun ID d'assessment (CF_RA_Id) trouvé pour cette ligne.");
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${config.API_BASE_URL}/moodys/assessment/refresh`, {
                cfRaId,
                tableName: selectedTable,
                id
            });

            if (response.data.success) {
                const status = response.data.data.CF_RA_Status || 'Updated';
                setSuccessMessage(`Statut rafraîchi avec succès. Nouveau statut: ${status}`);

                fetchData(); // Refresh the table
            }
        } catch (err) {
            console.error("Refresh error:", err);
            setError(err.response?.data?.error || "Erreur lors du rafraîchissement du statut.");
        } finally {
            setLoading(false);
            setSelectedRow(null);
        }
    };

    const handleViewScreening = async (row) => {
        if (!row) return;
        const cfRaId = row['cf_ra_id'] || row['CF_RA_Id'];

        if (!cfRaId) {
            setError("Aucun ID d'assessment (CF_RA_Id) disponible pour ce fournisseur. Veuillez d'abord lancer une évaluation.");
            return;
        }

        setSelectedRow(row);
        setLoadingScreening(true);
        setScreeningModalOpen(true);
        setScreeningDetails(null);

        try {
            const response = await axios.get(`${config.API_BASE_URL}/moodys/assessment/${cfRaId}`);
            if (response.data.success) {
                setScreeningDetails(response.data.data);
            } else {
                setError("Échec de la récupération des détails de screening.");
                setScreeningModalOpen(false);
            }
        } catch (err) {
            console.error("Screening view error:", err);
            setError(err.response?.data?.error || "Erreur lors de la récupération des détails.");
            setScreeningModalOpen(false);
        } finally {
            setLoadingScreening(false);
        }
    };

    const handleEditTierClick = (row) => {
        setEditingTierRow(row);
        setNewTierValue(row['nature_de_tiers'] || row['Nature de tiers'] || '');
        setEditTierModalOpen(true);
    };

    const saveTierNature = async () => {
        if (!editingTierRow || !selectedTable) return;

        setLoading(true);
        try {
            await axios.put(`${config.API_BASE_URL}/fournisseurs/${selectedTable}/${editingTierRow.id}`, {
                nature_de_tiers: newTierValue
            });
            setSuccessMessage("Nature de tiers mise à jour avec succès.");
            setEditTierModalOpen(false);
            setEditingTierRow(null);
            fetchData();
        } catch (err) {
            console.error("Update error:", err);
            setError("Erreur lors de la mise à jour.");
        } finally {
            setLoading(false);
        }
    };



    const extractMatchSummary = (details, category) => {
        if (!details) return "Non vérifié";

        // 1. Check if we have specific summary fields (flat legacy)
        const catLower = category.toLowerCase();
        if (details[`${catLower}_summary`]) return details[`${catLower}_summary`];

        // 2. Consolidate ALL checks/items from both standard and nested sources
        let allItems = [];

        // Helper: Traverse standard recursive structures (RiskAssessmentItems)
        const findItemsRecursive = (obj) => {
            if (!obj || typeof obj !== 'object') return;
            if (Array.isArray(obj)) {
                obj.forEach(val => findItemsRecursive(val));
                return;
            }
            if (Array.isArray(obj.RiskAssessmentItems)) {
                allItems = [...allItems, ...obj.RiskAssessmentItems];
            }
            if (Array.isArray(obj.RiskAssessmentProcesses)) {
                obj.RiskAssessmentProcesses.forEach(proc => {
                    if (Array.isArray(proc.RiskAssessmentItems)) {
                        allItems = [...allItems, ...proc.RiskAssessmentItems];
                    }
                    findItemsRecursive(proc);
                });
            }
            Object.values(obj).forEach(val => {
                if (val && typeof val === 'object' && !Array.isArray(val)) {
                    findItemsRecursive(val);
                }
            });
        };

        // Helper: Traverse the new nested structure (CFE_RAS)
        const findNestedChecks = (obj) => {
            if (!obj) return;

            if (obj.CFE_RAS && Array.isArray(obj.CFE_RAS)) {
                obj.CFE_RAS.forEach(rasItem => {
                    // Parallel arrays check
                    if (rasItem.CF_RAS_SubProcessId && Array.isArray(rasItem.CF_RAS_SubProcessId)) {
                        
                        // Structure is: [ ProcessBlock [ SubBlock [ ItemIds ] ] ]
                        rasItem.CF_RAS_SubProcessId.forEach((processBlock, pIdx) => {
                            if (!Array.isArray(processBlock)) return;
                            
                            processBlock.forEach((subBlock, sIdx) => {
                                if (!Array.isArray(subBlock)) return;
                                
                                const scores = (rasItem.CF_RAS_Score && rasItem.CF_RAS_Score[pIdx] && rasItem.CF_RAS_Score[pIdx][sIdx]) || [];
                                const statuses = (rasItem.CF_RAS_Status && rasItem.CF_RAS_Status[pIdx] && rasItem.CF_RAS_Status[pIdx][sIdx]) || [];
                                
                                subBlock.forEach((id, itemIdx) => {
                                    allItems.push({
                                        CF_RAS_SubProcessId: id,
                                        CF_RAS_Score: scores[itemIdx] !== undefined ? scores[itemIdx] : null,
                                        CF_RAS_Status: statuses[itemIdx] !== undefined ? statuses[itemIdx] : null,
                                        CF_RAS_Description: rasItem.CF_RAS_Description || ""
                                    });
                                });
                            });
                        });
                    } else {
                        // Fallback for flat structure or other variations
                        Object.values(rasItem).forEach(val => {
                            if (val && typeof val === 'object') findNestedChecks(val);
                        });
                    }
                });
            } else if (typeof obj === 'object') {
                Object.values(obj).forEach(val => {
                    if (val && typeof val === 'object') findNestedChecks(val);
                });
            }
        };

        // Collect from standard structure
        findItemsRecursive(details);

        // Collect from new nested structure
        if (details.screeningCheckDetails) {
            findNestedChecks(details.screeningCheckDetails);
        }

        // 3. Search in the consolidated list
        if (allItems.length > 0) {
            const categoryMap = {
                'sanction': ['sanction', 'liste de sanction', 'sanctions'], // Removed RDC/Woco to avoid false positives
                'watchlist': ['watchlist', 'monitor', 'surveillance', 'subjectcompanyrdc', 'controllingshareholdersrdc'],
                'media': ['media', 'presse', 'news', 'adverse', 'subjectcompanyrdc', 'controllingshareholdersrdc'], // RDC often covers media
                'pep': ['pep', 'politically'] // Removed directorsrdc (Score 75 is not a match)
            };

            const searchTerms = categoryMap[catLower] || [catLower];

            const item = allItems.find(it => {
                const label = (
                    it.CF_RAS_SubProcessId ||
                    it.CF_RAS_Description ||
                    it.CF_Process_ID ||
                    it.CF_Process_Label ||
                    it.CF_RAS_ID ||
                    ""
                ).toLowerCase();

                return searchTerms.some(term => label.includes(term));
            });

            if (item) {
                // Priority 1: Explicit Value
                if (item.CF_RA_Value) return item.CF_RA_Value;

                // Priority 2: Computed Score Key (High, Medium, etc.)
                if (item.CF_RAS_ComputedScoreKey) return item.CF_RAS_ComputedScoreKey;

                // Priority 3: Numerical Score interpretation
                // 100 = Match, 0 = No Match
                if (item.CF_RAS_Score !== undefined && item.CF_RAS_Score !== null) {
                    const score = parseInt(item.CF_RAS_Score);
                    if (score >= 100) return "Match à confirmer (High Risk)"; // Aligned with "Match to be confirmed"
                    if (score > 0) return "Correspondance partielle";
                    return "Aucune correspondance confirmée"; // 0
                }

                if (item.CF_RAS_ScoreKey) return item.CF_RAS_ScoreKey;
            }
        }

        return "Aucune correspondance confirmée";
    };

    /**
     * Extracts score and status for associated entities (Directors, BO, Shareholders)
     */
    const getAssociateScore = (details, associateType, categoryKey) => {
        if (!details) return { summary: "Non vérifié", hasMatches: false };

        let allItems = [];

        // Helper: Traverse recursive structures
        const findItemsRecursive = (obj) => {
            if (!obj || typeof obj !== 'object') return;
            if (Array.isArray(obj)) {
                obj.forEach(val => findItemsRecursive(val));
                return;
            }
            if (Array.isArray(obj.RiskAssessmentItems)) {
                allItems = [...allItems, ...obj.RiskAssessmentItems];
            }
            Object.values(obj).forEach(val => {
                if (val && typeof val === 'object' && !Array.isArray(val) && (val.RiskAssessmentItems || val.RiskAssessmentProcesses)) {
                    findItemsRecursive(val);
                }
            });
        };

        // Helper: Traverse nested checks (CFE_RAS)
        const findNestedChecks = (obj) => {
            if (!obj) return;
            if (obj.CFE_RAS && Array.isArray(obj.CFE_RAS)) {
                obj.CFE_RAS.forEach(rasItem => {
                    if (rasItem.CF_RAS_SubProcessId && Array.isArray(rasItem.CF_RAS_SubProcessId)) {
                        rasItem.CF_RAS_SubProcessId.forEach((processBlock, pIdx) => {
                            if (!Array.isArray(processBlock)) return;
                            processBlock.forEach((subBlock, sIdx) => {
                                if (!Array.isArray(subBlock)) return;
                                const scores = (rasItem.CF_RAS_Score && rasItem.CF_RAS_Score[pIdx] && rasItem.CF_RAS_Score[pIdx][sIdx]) || [];
                                const statuses = (rasItem.CF_RAS_Status && rasItem.CF_RAS_Status[pIdx] && rasItem.CF_RAS_Status[pIdx][sIdx]) || [];
                                subBlock.forEach((id, itemIdx) => {
                                    allItems.push({
                                        CF_RAS_SubProcessId: id,
                                        CF_RAS_Score: scores[itemIdx] !== undefined ? scores[itemIdx] : null,
                                        CF_RAS_Status: statuses[itemIdx] !== undefined ? statuses[itemIdx] : null
                                    });
                                });
                            });
                        });
                    }
                });
            } else if (typeof obj === 'object') {
                Object.values(obj).forEach(val => {
                    if (val && typeof val === 'object') findNestedChecks(val);
                });
            }
        };

        findItemsRecursive(details);
        if (details.screeningCheckDetails) findNestedChecks(details.screeningCheckDetails);

        // Map associate types to SubProcessIds
        const associateMap = {
            'directors': ['directorsrdc', 'directors'],
            'beneficial': ['beneficialownerrdc', 'beneficialownerscountry', 'beneficialowners'],
            'shareholders': ['controllingshareholdersrdc', 'controllingshareholderscountry', 'shareholders']
        };
        const searchTerms = associateMap[associateType.toLowerCase()] || [];

        // Match the specific associate type and category
        const items = allItems.filter(it => {
            const id = (it.CF_RAS_SubProcessId || "").toLowerCase();
            const typeMatch = searchTerms.some(term => id.includes(term));
            
            if (!typeMatch) return false;

            const score = parseInt(it.CF_RAS_Score || 0);
            if (score === 0) return false;

            // If the ID is specific (e.g. "DirectorsSanction"), use it
            if (id.includes(categoryKey.toLowerCase())) return true;
            if (categoryKey === 'pep' && (id.includes('politically') || id.includes('pep'))) return true;
            if (categoryKey === 'sanction' && (id.includes('sanction') || id.includes('liste'))) return true;

            // Heuristic for consolidated "RDC" checks based on common Moody's scoring:
            // 100+ : Sanction
            // 70-99: PEP
            // < 70 : Watchlist / Media (General risk)
            if (id.includes('rdc')) {
                if (categoryKey === 'sanction') return score >= 100;
                if (categoryKey === 'pep') return score >= 70 && score < 100;
                if (categoryKey === 'watchlist') return score > 0 && score < 70;
                if (categoryKey === 'media') return score > 0 && score < 70; // Often overlaps with watchlist in RDC
                return false;
            }

            // Default: if no specific indicator, show in its category if score > 0
            return true;
        });

        if (items.length === 0) return { summary: "Aucun match", hasMatches: false };

        let maxScore = 0;
        items.forEach(it => {
            const score = parseInt(it.CF_RAS_Score || 0);
            if (score > maxScore) maxScore = score;
        });

        const hasMatches = maxScore > 0;
        const summary = maxScore >= 100 ? "Match confirmé" : "Correspondance partielle";

        return { summary, hasMatches };
    };

    const renderAssociateBlock = (details, categoryLabel, categoryKey) => {
        const associates = [
            { label: 'Directors and Managers', key: 'directors' },
            { label: 'Beneficial Owners', key: 'beneficial' },
            { label: 'Controlling shareholders', key: 'shareholders' }
        ];

        return (
            <Box sx={{ flex: 1, minWidth: '250px', mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#555' }}>
                    {categoryLabel}
                </Typography>
                {associates.map((assoc, idx) => {
                    const { summary, hasMatches } = getAssociateScore(details, assoc.key, categoryKey);
                    return (
                        <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{assoc.label}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {hasMatches ? (
                                    <HelpOutlineIcon sx={{ color: '#9c27b0', fontSize: '1rem' }} />
                                ) : (
                                    <CheckCircleOutlineIcon sx={{ color: '#bdbdbd', fontSize: '1rem' }} />
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        );
    };



    // Color coding for Loi Sapin II Score (1-10 scale)
    const renderSapinScoreChip = (value) => {
        const num = parseInt(value);
        if (isNaN(num) || num < 1) return value;

        let color = '#e0e0e0';
        let label = '';
        let textColor = 'white';

        // Color mapping based on score
        if (num === 1 || num === 2) {
            color = '#00B050'; // Dark green - Risque très faible
            label = 'Risque très faible';
        } else if (num === 3) {
            color = '#92D050'; // Light green - Risque faible
            label = 'Risque faible';
        } else if (num === 4 || num === 5) {
            color = '#FFFF00'; // Yellow - Risque faible
            label = 'Risque faible';
            textColor = 'black';
        } else if (num === 6 || num === 7) {
            color = '#FFC000'; // Orange - Risque modéré
            label = 'Risque modéré';
        } else if (num >= 8) {
            color = '#FF0000'; // Red - Risque élevé
            label = 'Risque élevé';
        }

        return (
            <Chip
                label={`${num}`}
                sx={{ backgroundColor: color, color: textColor, fontWeight: 'bold', height: 24 }}
            />
        );
    };

    const renderStatusChip = (value) => {
        if (!value || value === '-') return '-';

        let color = '#e0e0e0';
        let textColor = 'white';
        let label = value;

        const valLower = value.toLowerCase();
        if (valLower === 'in progress') {
            color = '#FFC000'; // Orange
        } else if (valLower === 'accepted') {
            color = '#00B050'; // Green
        } else if (valLower === 'started') {
            color = '#1976d2'; // Blue
        } else if (valLower === 'finalized' || valLower === 'completed') {
            color = '#4caf50'; // Standard green
        }

        return (
            <Chip
                label={label}
                size="small"
                sx={{
                    backgroundColor: color,
                    color: textColor,
                    fontWeight: 'bold',
                    minWidth: '100px',
                    fontSize: '0.75rem'
                }}
            />
        );
    };

    // Color coding for Moody's Risk Score (0-100 scale)
    const renderScoreChip = (value) => {
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        let color = '#e0e0e0';
        let label = 'UNKNOWN';
        let textColor = 'white';

        if (num <= 30) {
            color = '#ACD149'; // Green
            label = 'LOW';
        } else if (num <= 60) {
            color = '#FF9900'; // Orange
            label = 'MEDIUM';
        } else {
            color = '#CC0000'; // Red
            label = 'HIGH';
        }

        return (
            <Chip
                label={`${num} ${label}`}
                sx={{ backgroundColor: color, color: textColor, fontWeight: 'bold', height: 24 }}
            />
        );
    };

    const handleOpenInMoodys = (row) => {
        if (!row) return;
        const cfRaId = row['cf_ra_id'] || row['CF_RA_Id'];
        const cfPortfolioId = row['cf_portfolio_id'] || row['CF_PORTFOLIO_ID'];
        const bvdId = row['bvd_id'] || row['BvD_ID'] || row['BvDID'];

        const host = "https://compliancecatalyst2-r1.bvdinfo.com";

        let url = "";
        if (cfRaId) {
            // Direct PDF download using CF_RA_Id
            url = `${host}/compliancecatalyst4/CompaniesPortfolio/RiskAssessments/Guid?guid=${cfRaId}&export=pdf&restart=1`;
        } else if (cfPortfolioId) {
            // TODO: Portfolio page URL (to be implemented)
            setError("Téléchargement PDF disponible uniquement pour les assessments déjà lancés. Veuillez d'abord démarrer l'évaluation.");
            return;
        } else if (bvdId) {
            setError("Téléchargement PDF disponible uniquement pour les assessments déjà lancés. Veuillez d'abord démarrer l'évaluation.");
            return;
        }

        if (url) {
            window.open(url, '_blank');
        } else {
            setError("Identifiant d'assessment manquant (CF_RA_Id). Veuillez d'abord démarrer l'évaluation.");
        }
    };

    const handleScoreClick = (event) => {
        setAnchorElScore(event.currentTarget);
    };

    const handleScoreClose = () => {
        setAnchorElScore(null);
    };

    const handleCalculateScores = async (version = 'v2') => {
        if (!selectedTable) return;
        handleScoreClose();

        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const response = await axios.post(`${config.API_BASE_URL}/analysis/calculate-scores`, {
                tableName: selectedTable,
                version
            });

            setSuccessMessage(response.data.message || "Scores calculés avec succès !");
            fetchData(); // Refresh the table to see new scores
        } catch (err) {
            console.error("Score calculation error:", err);
            setError(err.response?.data?.error || "Erreur lors du calcul des scores.");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenPortfolio = (row) => {
        if (!row) return;

        const bvdId = row.bvd_id ?? row.BvD_ID ?? row.BvDID;
        const cfPortfolioId = row.cf_portfolio_id ?? row.CF_PORTFOLIO_ID;

        console.log('--- handleOpenPortfolio ---');
        console.log('Row ID:', row.id, 'BvD_ID:', bvdId, 'CF_PORTFOLIO_ID:', cfPortfolioId);

        const host = "https://compliancecatalyst2-r1.bvdinfo.com";
        const basePath = "compliancecatalyst4"; // Lowercase standard

        let url = "";
        if (bvdId) {
            // Use cleaner, version-independent URL structure with session reset
            url = `${host}/${basePath}/CompaniesPortfolio/Report?bvdid=${bvdId}&restart=1&sl=${Date.now()}`;
        } else if (cfPortfolioId) {
            url = `${host}/${basePath}/CompaniesPortfolio/Report?PortfolioId=${cfPortfolioId}&restart=1&sl=${Date.now()}`;

        }

        console.log('Final Portfolio URL:', url);

        if (url) {
            window.open(url, '_blank');
        } else {
            setError("Identifiant Moody's (BvD ID) manquant. Veuillez d'abord ajouter le fournisseur au portfolio.");
        }
    };

    const customRenderers = {
        'nature_de_tiers': (value, row) => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', maxWidth: '85%' }} title={value}>
                    {value}
                </span>
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleEditTierClick(row);
                    }}
                    sx={{ ml: 1, padding: 0.5 }}
                >
                    <EditIcon fontSize="small" color="primary" />
                </IconButton>
            </Box>
        ),
        'Nature de tiers': (value, row) => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', maxWidth: '85%' }} title={value}>
                    {value}
                </span>
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleEditTierClick(row);
                    }}
                    sx={{ ml: 1, padding: 0.5 }}
                >
                    <EditIcon fontSize="small" color="primary" />
                </IconButton>
            </Box>
        ),
        'Score': renderSapinScoreChip,
        'SCORE': renderSapinScoreChip,
        'score': renderSapinScoreChip,
        'Note': renderSapinScoreChip,
        'NOTE': renderSapinScoreChip,
        'note': renderSapinScoreChip,
        'note v3': renderSapinScoreChip,
        'note v4': renderSapinScoreChip,
        'Note v3': renderSapinScoreChip,
        'Note v4': renderSapinScoreChip,
        'Score de Risque': renderScoreChip,
        'cf_ra_score': renderScoreChip,
        'assessment_status': (value, row) => {
            const status = row.cf_ra_status || '-';
            return renderStatusChip(status);
        },
        'Statut Assessment': renderStatusChip,
        'cf_ra_status': renderStatusChip,
        'assessment_risk_score': (value, row) => {
            const dbScore = row.cf_ra_score || row.cf_ra_scorekey_cf_scoreandclasses_label;
            if (dbScore !== null && dbScore !== undefined && dbScore !== '') {
                // If it looks like a label from the DB, convert to chip structure
                if (typeof dbScore === 'string' && (dbScore.includes('HIGH') || dbScore.includes('MEDIUM') || dbScore.includes('LOW'))) {
                   return (
                        <Chip
                            label={dbScore}
                            sx={{
                                backgroundColor: dbScore.toUpperCase().includes('HIGH') ? '#CC0000' : (dbScore.toUpperCase().includes('MEDIUM') ? '#FF9900' : '#ACD149'),
                                color: 'white',
                                fontWeight: 'bold',
                                height: 24
                            }}
                        />
                    );
                }
                return renderScoreChip(dbScore);
            }
            return '-';
        },
        'assessment_completion_date': (value, row) => {
            const dateStr = row.cf_ra_date;
            if (!dateStr) return '-';

            try {
                return new Date(dateStr).toLocaleDateString('fr-FR');
            } catch (e) {
                return dateStr;
            }
        },
        'Actions': (value, row) => (
            <IconButton size="small" onClick={(e) => handleRowActionClick(e, row)}>
                <MoreVertIcon fontSize="small" />
            </IconButton>
        )
    };

    const handleTableChange = (event) => {
        const newTable = event.target.value;
        setSelectedTable(newTable);
        if (!newTable.startsWith('view_')) {
            setLastSourceTable(newTable);
        }
        setPage(1);
        setFilters({});
        setSortConfig({});
        setSelectedRows([]); // Clear selection when table changes
        setIsGlobalSelection(false);
        setError(null);
        setSuccessMessage(null);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setPage(1);
    };

    const handleSort = (column, direction) => {
        setSortConfig({ key: column, direction });
    };

    const handleFilter = (newFilters) => {
        setFilters(newFilters);
        setPage(1);
    };

    const handleCreateGroup = async () => {
        if (!newGroupName) return;

        try {
            await axios.post(`${config.API_BASE_URL}/groups`, {
                name: newGroupName,
                sourceTable: selectedTable,
                filters: filters
            });
            setSuccessMessage(`Group "${newGroupName}" created successfully!`);
            setOpenGroupDialog(false);
            setNewGroupName('');
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.error || 'Failed to create group');
        }
    };

    const ActionsCell = ({ row }) => {
        const [anchorEl, setAnchorEl] = useState(null);
        const open = Boolean(anchorEl);

        const handleClick = (event) => {
            setAnchorEl(event.currentTarget);
        };

        const handleClose = () => {
            setAnchorEl(null);
        };

        return (
            <Box>
                <IconButton size="small" onClick={handleClick}>
                    <MoreVertIcon fontSize="small" />
                </IconButton>
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <MenuItem
                        onClick={() => {
                            handleAddToPortfolio(row);
                            handleClose();
                        }}
                        sx={{ fontSize: '0.8125rem' }}
                    >
                        <AccountTreeIcon sx={{ mr: 1, fontSize: '1rem' }} />
                        Ajouter au Portfolio
                    </MenuItem>
                </Menu>
            </Box>
        );
    };

    const renderViewActions = () => (
        <>
            <Button
                variant="outlined"
                size="small"
                onClick={() => setOpenViewDialog(true)}
                disabled={!selectedTable}
                startIcon={<AddIcon />}
                sx={{ borderRadius: 2, textTransform: 'none', mr: 1, height: 32 }}
            >
                Create View
            </Button>
            <FormControl size="small" sx={{ minWidth: 150, mr: 1 }}>
                <InputLabel id="view-select-label">Select View</InputLabel>
                <Select
                    labelId="view-select-label"
                    label="Select View"
                    value={(typeof selectedTable === 'string' && selectedTable.startsWith('view_')) ? selectedTable : "none"}
                    onChange={(e) => {
                        if (e.target.value === "none") {
                            const fallbackTable = (tables.find(t => t.name?.startsWith('fournisseurs_'))?.name) || '';
                            setSelectedTable((typeof lastSourceTable === 'string' ? lastSourceTable : lastSourceTable?.name) || (typeof defaultTable === 'string' ? defaultTable : defaultTable?.name) || fallbackTable);
                        } else {
                            if (typeof selectedTable === 'string' && !selectedTable.startsWith('view_')) {
                                setLastSourceTable(selectedTable);
                            }
                            setSelectedTable(e.target.value);
                        }
                    }}
                    sx={{ borderRadius: 2, height: 32 }}
                    renderValue={(val) => {
                        if (val === 'none') return <em>None (All Data)</em>;
                        const t = tables.find(t => t.name === val);
                        if (!t) return val;
                        return <>{t.name === defaultTable && '⭐ '}{t.name.replace('view_', '')}</>;
                    }}
                >
                    <MenuItem value="none">
                        <em>None (All Data)</em>
                    </MenuItem>
                    {tables.filter(t => t.name?.startsWith('view_') && t.source === (lastSourceTable || (selectedTable?.startsWith('view_') ? "" : selectedTable))).map((table) => (
                        <MenuItem key={table.name} value={table.name} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {table.name === defaultTable && '⭐ '}{table.name.replace('view_', '')}
                            </Box>
                            <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteTable(table.name);
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </>
    );

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">Supplier Data</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => setOpenGroupDialog(true)}
                        disabled={!selectedTable}
                    >
                        Create Group
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleScoreClick}
                        disabled={!selectedTable || loading}
                        startIcon={<RefreshIcon />}
                    >
                        Calculer les scores
                    </Button>
                    <Menu
                        anchorEl={anchorElScore}
                        open={Boolean(anchorElScore)}
                        onClose={handleScoreClose}
                    >
                        <MenuItem onClick={() => handleCalculateScores('v2')}>
                            <Box>
                                <Typography variant="body1">Version v2 (Standard 2026)</Typography>
                                <Typography variant="caption" color="text.secondary">Workday 2026 + Logique Régionale</Typography>
                            </Box>
                        </MenuItem>
                        <MenuItem onClick={() => handleCalculateScores('v3')}>
                            <Box>
                                <Typography variant="body1">Version v3 (Réplique Macro)</Typography>
                                <Typography variant="caption" color="text.secondary">Respect strict des règles historiques VBA</Typography>
                            </Box>
                        </MenuItem>
                        <MenuItem onClick={() => handleCalculateScores('v4')}>
                            <Box>
                                <Typography variant="body1">Version v4 (IPC 2025)</Typography>
                                <Typography variant="caption" color="text.secondary">Poids spécifiques par pays basés sur l'IPC 2025</Typography>
                            </Box>
                        </MenuItem>
                    </Menu>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            const params = new URLSearchParams();
                            if (filters && Object.keys(filters).length > 0) {
                                params.append('filters', JSON.stringify(filters));
                            }
                            window.open(`${config.API_BASE_URL}/fournisseurs/${selectedTable}/export?${params.toString()}`, '_blank');
                        }}
                        disabled={!selectedTable}
                    >
                        Export Excel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={(e) => setAnchorElBulk(e.currentTarget)}
                        disabled={selectedRows.length === 0 && !isGlobalSelection}
                        startIcon={<AccountTreeIcon />}
                    >
                        Actions groupées ({isGlobalSelection ? total : selectedRows.length})
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleSetDefaultTable}
                        disabled={!selectedTable}
                        startIcon={<StarIcon />}
                    >
                        Définir par défaut
                    </Button>
                    <Menu
                        anchorEl={anchorElBulk}
                        open={Boolean(anchorElBulk)}
                        onClose={() => setAnchorElBulk(null)}
                    >
                        <MenuItem onClick={handleBulkAddToPortfolio}>
                            <AccountTreeIcon sx={{ mr: 1, fontSize: '1rem' }} />
                            Ajouter au Portfolio Moody's
                        </MenuItem>
                        <MenuItem onClick={() => { setAnchorElBulk(null); setAssessmentModalOpen(true); }}>
                            <PlayArrowIcon sx={{ mr: 1, fontSize: '1rem' }} />
                            Démarrer l'évaluation
                        </MenuItem>
                        <MenuItem onClick={handleBulkRefreshStatus}>
                            <RefreshIcon sx={{ mr: 1, fontSize: '1rem', color: '#1976d2' }} />
                            Rafraîchir le statut Moody's
                        </MenuItem>
                        <MenuItem onClick={handleBulkCompleteAssessment}>
                            <CheckCircleIcon sx={{ mr: 1, fontSize: '1rem', color: '#4caf50' }} />
                            Finaliser l'évaluation
                        </MenuItem>
                    </Menu>
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel id="table-select-label">Select Table</InputLabel>
                        <Select
                            labelId="table-select-label"
                            value={(typeof selectedTable === 'string' && selectedTable.startsWith('view_')) ? (lastSourceTable || "") : selectedTable}
                            label="Select Table"
                            onChange={handleTableChange}
                        >
                            {tables.filter(t => !t.name.startsWith('view_')).map((table) => (
                                <MenuItem key={table.name} value={table.name}>
                                    {table.name === defaultTable && '⭐ '}{table.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

            {selectedTable ? (
                <DataTable
                    data={data}
                    columns={columns}
                    headerStyles={headerStyles}
                    total={total}
                    page={page}
                    limit={limit}
                    onPageChange={setPage}
                    onLimitChange={setLimit}
                    onSort={(key, direction) => setSortConfig({ key, direction })}
                    onFilter={setFilters}
                    onColumnVisibilityChange={setVisibleColumns}
                    loading={loading}
                    selectedRows={selectedRows}
                    onSelectionChange={setSelectedRows}
                    isGlobalSelection={isGlobalSelection}
                    onGlobalSelectionChange={setIsGlobalSelection}
                    customRenderers={customRenderers}
                    renderHeaderActions={renderViewActions}
                    onFetchUniqueValues={async (column) => {
                        const response = await axios.get(`${config.API_BASE_URL}/fournisseurs/${selectedTable}/values/${column}`);
                        return response.data;
                    }}
                    filterTypes={{
                        'cf_ra_status': 'select',
                        'assessment_status': 'select',
                        'nature_de_tiers': { type: 'select', options: TIER_NATURE_OPTIONS }
                    }}
                />
            ) : (
                <Alert severity="info">Please select or upload a table to view data.</Alert>
            )}

            <Dialog open={openGroupDialog} onClose={() => setOpenGroupDialog(false)}>
                <DialogTitle>Create New Group</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Create a group from the current table "{typeof selectedTable === 'string' ? selectedTable : selectedTable?.name}" with the currently applied filters.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Group Name"
                        fullWidth
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenGroupDialog(false)}>Cancel</Button>
                    <Button onClick={handleCreateGroup} variant="contained" color="primary">Create</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)}>
                <DialogTitle>Create New View</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Create a dynamic view from current filters. This will create a table named "view_{newViewName}".
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="View Name"
                        fullWidth
                        value={newViewName}
                        onChange={(e) => setNewViewName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenViewDialog(false)}>Cancel</Button>
                    <Button onClick={handleCreateView} variant="contained" color="primary">Create</Button>
                </DialogActions>
            </Dialog>

            <Menu
                anchorEl={anchorElRow}
                open={Boolean(anchorElRow)}
                onClose={handleRowActionClose}
            >
                <MenuItem onClick={() => { handleAddToPortfolio(selectedRow); handleRowActionClose(); }}>
                    <AccountTreeIcon sx={{ mr: 1, fontSize: '1rem' }} />
                    Ajouter au Portfolio
                </MenuItem>
                <MenuItem onClick={() => { handleRowActionClose(); setAssessmentModalOpen(true); }}>
                    <PlayArrowIcon sx={{ mr: 1, fontSize: '1rem' }} />
                    Démarrer l'évaluation
                </MenuItem>
                <MenuItem onClick={() => { handleCompleteAssessment(selectedRow); handleRowActionClose(); }}>
                    <CheckCircleIcon sx={{ mr: 1, fontSize: '1rem', color: '#4caf50' }} />
                    Finaliser l'évaluation
                </MenuItem>
                <MenuItem onClick={() => { handleOpenInMoodys(selectedRow); handleRowActionClose(); }}>
                    <LaunchIcon sx={{ mr: 1, fontSize: '1rem' }} />
                    Ouvrir le PDF Moody's
                </MenuItem>
                <MenuItem onClick={() => { handleOpenPortfolio(selectedRow); handleRowActionClose(); }}>
                    <FolderOpenIcon sx={{ mr: 1, fontSize: '1rem' }} />
                    Ouvrir le portfolio
                </MenuItem>
                <MenuItem onClick={() => { handleViewScreening(selectedRow); handleRowActionClose(); }}>
                    <StarIcon sx={{ mr: 1, fontSize: '1rem', color: '#ffc107' }} />
                    Voir screening
                </MenuItem>
                <MenuItem onClick={() => { handleRefreshStatus(selectedRow); handleRowActionClose(); }}>
                    <RefreshIcon sx={{ mr: 1, fontSize: '1rem', color: '#1976d2' }} />
                    Rafraîchir le statut Moody's
                </MenuItem>
            </Menu>

            <AssessmentModal
                open={assessmentModalOpen}
                onClose={() => setAssessmentModalOpen(false)}
                onSubmit={handleAssessmentSubmit}
                companyName={selectedRow ? (selectedRow['original_name_partner'] || selectedRow['partners'] || selectedRow['Nom'] || selectedRow['Name']) : 'Sélection groupée'}
            />

            <Dialog
                open={screeningModalOpen}
                onClose={() => setScreeningModalOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        minHeight: '60vh'
                    }
                }}
            >
                <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                            Détails du Screening Moody's
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            {selectedRow?.FOURNISSEUR || selectedRow?.Nom || selectedRow?.Name}
                        </Typography>
                    </Box>
                    {screeningDetails?.CF_RA_Status && renderStatusChip(screeningDetails.CF_RA_Status)}
                </DialogTitle>
                <DialogContent sx={{ p: 4 }}>
                    {loadingScreening ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <Typography>Chargement des détails de screening...</Typography>
                        </Box>
                    ) : screeningDetails ? (
                        <Box>
                            <Box sx={{ mb: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2, flex: 1, minWidth: '200px' }}>
                                    <Typography variant="overline" color="textSecondary">Score de Risque Global</Typography>
                                    <Box sx={{ mt: 1 }}>{renderScoreChip(screeningDetails.CF_RA_Score)}</Box>
                                </Box>
                                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2, flex: 1, minWidth: '200px' }}>
                                    <Typography variant="overline" color="textSecondary">ID Assessment</Typography>
                                    <Typography variant="h6" sx={{ fontSize: '1rem', wordBreak: 'break-all' }}>
                                        {screeningDetails.CF_RA_Id || screeningDetails.SelectionId?.CF_RA_Id || 'N/A'}
                                    </Typography>
                                </Box>
                                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2, flex: 1, minWidth: '200px' }}>
                                    <Typography variant="overline" color="textSecondary">Modèle utilisé</Typography>
                                    <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                                        {screeningDetails.AF_Model_Label || screeningDetails.CF_Model_Id || 'N/A'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Synthèse du Screening</Typography>
                            <Box sx={{ mb: 4, border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8f9fa', textAlign: 'left' }}>
                                            <th style={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>Catégorie</th>
                                            <th style={{ padding: '12px', borderBottom: '1px solid #e0e0e0', textAlign: 'center' }}>Résultat / Simulation</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            { label: 'Sanction list', key: 'sanction' },
                                            { label: 'Watchlist', key: 'watchlist' },
                                            { label: 'Media', key: 'media' },
                                            { label: 'PEP', key: 'pep' }
                                        ].map((cat, idx) => {
                                            const summary = extractMatchSummary(screeningDetails, cat.key);
                                            const hasMatches = summary.toLowerCase().includes('match to be confirmed') ||
                                                summary.toLowerCase().includes('confirmed match') ||
                                                summary.toLowerCase().includes('match confirmé') ||
                                                summary.toLowerCase().includes('match à confirmer') ||
                                                summary.toLowerCase().includes('correspondance') && !summary.startsWith('0') && !summary.toLowerCase().includes('aucune');

                                            return (
                                                <tr key={idx}>
                                                    <td style={{ padding: '12px', borderBottom: '1px solid #e0e0e0', fontWeight: 500 }}>{cat.label}</td>
                                                    <td style={{ padding: '12px', borderBottom: '1px solid #e0e0e0', textAlign: 'center' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                            <Typography variant="body2" color={hasMatches ? "error" : "textSecondary"}>
                                                                {summary}
                                                            </Typography>
                                                            {hasMatches ? (
                                                                <HelpOutlineIcon sx={{ color: '#9c27b0', fontSize: '1.2rem' }} />
                                                            ) : (
                                                                <CheckCircleOutlineIcon sx={{ color: '#bdbdbd', fontSize: '1.2rem' }} />
                                                            )}
                                                        </Box>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </Box>

                            <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span>Screening - Associated entities and individuals</span>
                                <KeyboardArrowUpIcon />
                            </Typography>
                            <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                                <Grid container spacing={4}>
                                    <Grid item xs={12} md={4}>
                                        {renderAssociateBlock(screeningDetails, 'Sanction list', 'sanction')}
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        {renderAssociateBlock(screeningDetails, 'Watchlist', 'watchlist')}
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        {renderAssociateBlock(screeningDetails, 'Media', 'media')}
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        {renderAssociateBlock(screeningDetails, 'PEP', 'pep')}
                                    </Grid>
                                </Grid>
                            </Box>

                            <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>Toutes les informations récupérées</Typography>
                            <Box sx={{
                                bgcolor: '#1e1e1e',
                                color: '#d4d4d4',
                                p: 2,
                                borderRadius: 1,
                                overflow: 'auto',
                                maxHeight: '400px',
                                fontFamily: 'monospace',
                                fontSize: '0.85rem'
                            }}>
                                <pre>{JSON.stringify(screeningDetails, null, 2)}</pre>
                            </Box>
                        </Box>
                    ) : (
                        <Alert severity="info">Aucun détail disponible pour ce fournisseur.</Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
                    <Button onClick={() => setScreeningModalOpen(false)} variant="contained" color="primary">
                        Fermer
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={editTierModalOpen} onClose={() => setEditTierModalOpen(false)}>
                <DialogTitle>Mettre à jour la Nature de tiers</DialogTitle>
                <DialogContent sx={{ minWidth: 400, pt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Sélectionnez la nouvelle valeur pour ce fournisseur.
                    </Typography>
                    <FormControl fullWidth margin="dense">
                        <InputLabel id="tier-nature-select-label">Nature de tiers</InputLabel>
                        <Select
                            labelId="tier-nature-select-label"
                            value={newTierValue}
                            label="Nature de tiers"
                            onChange={(e) => setNewTierValue(e.target.value)}
                        >
                            <MenuItem value="">
                                <em>Aucune</em>
                            </MenuItem>
                            {TIER_NATURE_OPTIONS.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditTierModalOpen(false)}>Annuler</Button>
                    <Button onClick={saveTierNature} variant="contained" color="primary">
                        Enregistrer
                    </Button>
                </DialogActions>
            </Dialog>
        </Box >
    );
};

export default Home;
